'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Calendar, Tag, Filter, X } from 'lucide-react';

type BlogPost = {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  featured_image?: string;
  thumbnail_image?: string;
  tags: string[];
  published_at?: string;
  created_at: string;
};

// Extract first image URL from content (looks for [IMAGE: url] or [IMAGE: url | alt] pattern)
function getFirstContentImage(content: string): string | null {
  const match = content.match(/\[IMAGE:\s*([^\]|]+)/);
  return match ? match[1].trim() : null;
}

// Get the thumbnail image for a post (thumbnail_image, featured_image, or first content image)
function getThumbnailImage(post: BlogPost): string | null {
  if (post.thumbnail_image) return post.thumbnail_image;
  if (post.featured_image) return post.featured_image;
  return getFirstContentImage(post.content);
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function BlogGrid({ posts }: { posts: BlogPost[] }) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Extract all unique tags from posts
  const allCategories = useMemo(() => {
    const tagSet = new Set<string>();
    posts.forEach(post => {
      (post.tags || []).forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [posts]);

  // Filter posts by selected category
  const filteredPosts = useMemo(() => {
    if (!selectedCategory) return posts;
    return posts.filter(post =>
      (post.tags || []).includes(selectedCategory)
    );
  }, [posts, selectedCategory]);

  // Category filter content - reused in both sidebar and mobile modal
  const CategoryFilters = ({ onSelect }: { onSelect?: () => void }) => (
    <div className="space-y-2">
      {/* All posts option */}
      <button
        onClick={() => {
          setSelectedCategory(null);
          onSelect?.();
        }}
        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
          selectedCategory === null
            ? 'bg-brand-teal text-white'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        All Posts
        <span className="ml-2 text-xs opacity-70">({posts.length})</span>
      </button>

      {/* Category options */}
      {allCategories.map((category) => {
        const count = posts.filter(p => (p.tags || []).includes(category)).length;
        return (
          <button
            key={category}
            onClick={() => {
              setSelectedCategory(category);
              onSelect?.();
            }}
            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
              selectedCategory === category
                ? 'bg-brand-teal text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {category}
            <span className="ml-2 text-xs opacity-70">({count})</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      {/* Mobile Filter Modal */}
      {isFilterOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsFilterOpen(false)}
          />
          {/* Modal */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-5 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900 flex items-center">
                <Filter className="h-4 w-4 mr-2" />
                Filter by Category
              </h3>
              <button
                onClick={() => setIsFilterOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <CategoryFilters onSelect={() => setIsFilterOpen(false)} />
          </div>
        </div>
      )}

      <div className="grid gap-8 items-start lg:grid-cols-[16rem_1fr]">
        {/* Sidebar - Categories (hidden on mobile) */}
        <aside className="hidden lg:block">
          <div className="lg:sticky lg:top-24 bg-white rounded-lg border border-gray-200 p-5">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              Categories
            </h3>
            <CategoryFilters />
          </div>
        </aside>

      {/* Blog Posts Grid */}
      <div>
        {/* Mobile Filter Button */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setIsFilterOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filter
            {selectedCategory && (
              <span className="ml-1 px-2 py-0.5 bg-brand-teal text-white text-xs rounded-full">
                {selectedCategory}
              </span>
            )}
          </button>
        </div>

        {filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-600 mb-4">
              No posts found
            </h2>
            <p className="text-gray-500">
              {selectedCategory
                ? `No posts in "${selectedCategory}" category yet.`
                : 'Check back soon for new content!'}
            </p>
            {selectedCategory && (
              <button
                onClick={() => setSelectedCategory(null)}
                className="mt-4 text-brand-teal hover:underline"
              >
                View all posts
              </button>
            )}
          </div>
        ) : (
          <>
            {selectedCategory && (
              <div className="mb-6 flex items-center justify-between">
                <p className="text-gray-600">
                  Showing {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''} in <span className="font-medium text-brand-teal">{selectedCategory}</span>
                </p>
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="text-sm text-gray-500 hover:text-brand-teal"
                >
                  Clear filter
                </button>
              </div>
            )}

            {/* Mobile: Horizontal list cards */}
            <div className="md:hidden space-y-3">
              {filteredPosts.map((post) => {
                const thumbnailImage = getThumbnailImage(post);
                return (
                  <Link key={post.id} href={`/blog/${post.slug}`}>
                    <article className="flex gap-3 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow p-3">
                      {/* Thumbnail */}
                      <div className="relative w-24 h-24 flex-shrink-0 rounded-md overflow-hidden">
                        {thumbnailImage ? (
                          <Image
                            src={thumbnailImage}
                            alt={post.title}
                            fill
                            className="object-cover object-top"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-brand-teal to-brand-teal/60 flex items-center justify-center">
                            <span className="text-white text-2xl font-bold">
                              {post.title.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h2 className="font-bold text-gray-900 text-sm leading-tight line-clamp-2 mb-1">
                          {post.title}
                        </h2>
                        {post.excerpt && (
                          <p className="text-gray-600 text-xs line-clamp-2 mb-2">
                            {post.excerpt}
                          </p>
                        )}
                        <div className="flex items-center text-xs text-gray-500">
                          <Calendar className="h-3 w-3 mr-1" />
                          {formatDate(post.published_at || post.created_at)}
                        </div>
                      </div>
                    </article>
                  </Link>
                );
              })}
            </div>

            {/* Desktop: Grid cards */}
            <div className="hidden md:grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <Link href={`/blog/${post.slug}`}>
                    {(() => {
                      const thumbnailImage = getThumbnailImage(post);
                      return thumbnailImage ? (
                        <div className="relative aspect-square w-full">
                          <Image
                            src={thumbnailImage}
                            alt={post.title}
                            fill
                            className="object-cover object-top"
                          />
                        </div>
                      ) : (
                        <div className="aspect-square bg-gradient-to-br from-brand-teal to-brand-teal/60 flex items-center justify-center">
                          <span className="text-white text-5xl font-bold">
                            {post.title.charAt(0)}
                          </span>
                        </div>
                      );
                    })()}
                  </Link>

                  <div className="p-5">
                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags.slice(0, 2).map((tag, index) => (
                          <button
                            key={index}
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedCategory(tag);
                            }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium bg-brand-teal/10 text-brand-teal rounded hover:bg-brand-teal/20 transition-colors"
                          >
                            <Tag className="h-3 w-3 mr-1" />
                            {tag}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Title */}
                    <Link href={`/blog/${post.slug}`}>
                      <h2 className="text-lg font-bold text-gray-900 mb-2 hover:text-brand-teal transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                    </Link>

                    {/* Excerpt */}
                    {post.excerpt && (
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {post.excerpt}
                      </p>
                    )}

                    {/* Date */}
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDate(post.published_at || post.created_at)}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}
